/**
 * Expandable Description Component
 * Handles show more/less functionality for long collection descriptions
 * while maintaining SEO friendliness by keeping full content in DOM
 */

class ExpandableDescription {
    constructor(element) {
        this.element = element;
        this.content = element.querySelector('.expandable-description__content');
        this.toggle = element.querySelector('.expandable-description__toggle');
        this.isExpanded = false;

        this.desktopLines = parseInt(element.dataset.desktopLines) || 4;
        this.mobileLines = parseInt(element.dataset.mobileLines) || 2;

        this.init();
    }

    init() {
        // Set CSS custom properties for line clamping
        this.updateCSSProperties();

        // Check if content overflows and needs truncation
        this.checkOverflow();

        // Add event listener for toggle button
        if (this.toggle) {
            this.toggle.addEventListener('click', () => this.toggleExpanded());
        }

        // Re-check overflow on window resize
        window.addEventListener('resize', () => {
            this.updateCSSProperties();
            this.checkOverflow();
        });
    }

    updateCSSProperties() {
        // Set CSS custom properties for current viewport
        this.element.style.setProperty('--desktop-lines', this.desktopLines);
        this.element.style.setProperty('--mobile-lines', this.mobileLines);
    }

    checkOverflow() {
        // Reset to collapsed state to measure
        const wasExpanded = this.isExpanded;
        if (wasExpanded) {
            this.collapse(false);
        }

        // Force a reflow to get accurate measurements
        this.content.offsetHeight;

        // Get the scroll height vs client height to detect overflow
        const isOverflowing = this.content.scrollHeight > this.content.clientHeight;

        if (isOverflowing) {
            this.element.classList.add('has-overflow');
        } else {
            this.element.classList.remove('has-overflow');
        }

        // Restore expanded state if it was expanded
        if (wasExpanded && isOverflowing) {
            this.expand(false);
        }
    }

    toggleExpanded() {
        if (this.isExpanded) {
            this.collapse();
        } else {
            this.expand();
        }
    }

    expand(animate = true) {
        this.isExpanded = true;
        this.element.setAttribute('aria-expanded', 'true');

        if (animate) {
            // Get the full height for smooth animation
            const currentHeight = this.content.clientHeight;
            this.element.setAttribute('aria-expanded', 'true');
            const expandedHeight = this.content.scrollHeight;

            // Set up animation
            this.content.style.maxHeight = currentHeight + 'px';
            this.content.style.transition = 'max-height 0.3s ease';

            // Trigger animation
            requestAnimationFrame(() => {
                this.content.style.maxHeight = expandedHeight + 'px';
            });

            // Clean up after animation
            setTimeout(() => {
                this.content.style.maxHeight = '';
                this.content.style.transition = '';
            }, 300);
        }
    }

    collapse(animate = true) {
        this.isExpanded = false;
        this.element.setAttribute('aria-expanded', 'false');

        if (animate) {
            // Get current height for smooth animation
            const currentHeight = this.content.scrollHeight;
            this.content.style.maxHeight = currentHeight + 'px';
            this.content.style.transition = 'max-height 0.3s ease';

            // Force reflow
            this.content.offsetHeight;

            // Trigger collapse animation
            requestAnimationFrame(() => {
                this.content.style.maxHeight = '';
            });

            // Clean up after animation
            setTimeout(() => {
                this.content.style.transition = '';
            }, 300);
        }
    }
}

// Initialize all expandable descriptions when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    const expandableDescriptions = document.querySelectorAll('.expandable-description');
    expandableDescriptions.forEach(element => {
        new ExpandableDescription(element);
    });
});

// Handle dynamic content loading (e.g., AJAX)
if (window.theme && window.theme.initExpandableDescriptions) {
    window.theme.initExpandableDescriptions = () => {
        const expandableDescriptions = document.querySelectorAll('.expandable-description:not([data-initialized])');
        expandableDescriptions.forEach(element => {
            element.setAttribute('data-initialized', 'true');
            new ExpandableDescription(element);
        });
    };
} else {
    window.theme = window.theme || {};
    window.theme.initExpandableDescriptions = () => {
        const expandableDescriptions = document.querySelectorAll('.expandable-description:not([data-initialized])');
        expandableDescriptions.forEach(element => {
            element.setAttribute('data-initialized', 'true');
            new ExpandableDescription(element);
        });
    };
}